import type {
  PdMeasurementResult,
  ShMeasurementResult,
} from '@ca/optical-measure/flow'
import { useOrderState } from '@order/hooks/useOrderState'
import useMeasureSdk, {
  type MeasurementLocationType,
} from 'hooks/measurements/useMeasureSdk'
import { useMeasurementLauncher } from './MeasurementContext'
import {
  useMeasurementMethod,
  type MeasurementMethod,
} from './useMeasurementMethod'

type MeasureArgs = { manual?: boolean }

type PdMeasureOptions = {
  location?: MeasurementLocationType
}

type ShMeasureOptions = {
  location?: MeasurementLocationType
  vboxMm?: number
  onComplete: (result: ShMeasurementResult) => void
}

type UseMeasureResult = {
  method: MeasurementMethod
  onMeasure: (args?: MeasureArgs) => void
}

export function useMeasure(
  type: 'pd',
  options?: PdMeasureOptions
): UseMeasureResult
export function useMeasure(
  type: 'sh',
  options: ShMeasureOptions
): UseMeasureResult
export function useMeasure(
  type: 'pd' | 'sh',
  options: PdMeasureOptions | ShMeasureOptions = {}
): UseMeasureResult {
  const method = useMeasurementMethod(type)
  const { open } = useMeasurementLauncher()
  const updatePd = useOrderState((state) => state.updatePd)
  const { onMeasure: onMeasureSdk } = useMeasureSdk(type, options.location)

  const handlePdComplete = (result: PdMeasurementResult) => {
    updatePd(
      {
        l_pd: result.leftPd,
        r_pd: result.rightPd,
      },
      'measurement'
    )
  }

  const onMeasure = (args?: MeasureArgs) => {
    if (method === 'disabled') return
    if (method === 'sdk') {
      onMeasureSdk(args)
      return
    }

    if (type === 'pd') {
      open({ type: 'pd', onComplete: handlePdComplete })
      return
    }

    const shOptions = options as ShMeasureOptions
    open({
      type: 'sh',
      vboxMm: shOptions.vboxMm,
      onComplete: shOptions.onComplete,
    })
  }

  return { method, onMeasure }
}
